import React, { useState } from "react";
import { useScene } from "../../hooks/useScenes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absolutize = (url) => {
  if (!url) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${BACKEND_URL}${url}`;
  return url;
};

const buildSrcset = (raw) => {
  if (!raw) return undefined;
  return raw
    .split(",")
    .map((s) => s.trim())
    .map((part) => {
      const [url, descriptor] = part.split(/\s+/);
      return `${absolutize(url)} ${descriptor || ""}`.trim();
    })
    .join(", ");
};

/**
 * Renders a branded lifestyle scene fetched from /api/scenes.
 *
 * Props:
 *  - scene: scene id (e.g. "hero-counter")
 *  - alt: alt text
 *  - className: applied to inner <img>
 *  - sizes: overrides the manifest sizes attribute
 *  - eager: above-the-fold flag
 *  - fallback: optional fallback URL while scene loads / on error
 *  - ...rest: forwarded to inner <img> (data-testid etc)
 */
export default function SceneImage({
  scene,
  alt = "",
  className = "",
  sizes,
  eager = false,
  fallback,
  ...rest
}) {
  const data = useScene(scene);
  const [errored, setErrored] = useState(false);

  const localSrc = absolutize(data?.src);
  const fallbackSrc = absolutize(fallback);

  if (!data && !fallbackSrc) {
    // Scene metadata still loading and we have no static fallback —
    // render a transparent placeholder of the same shape so layout doesn't jump.
    return <div className={className} aria-hidden="true" />;
  }

  const finalSrc = errored ? fallbackSrc || localSrc : localSrc || fallbackSrc;
  const webp = data && !errored ? buildSrcset(data.srcset_webp) : undefined;
  const jpeg = data && !errored ? buildSrcset(data.srcset_jpeg) : undefined;
  const finalSizes = sizes || data?.sizes || "100vw";

  return (
    <picture>
      {webp && <source type="image/webp" srcSet={webp} sizes={finalSizes} />}
      {jpeg && <source type="image/jpeg" srcSet={jpeg} sizes={finalSizes} />}
      <img
        src={finalSrc}
        alt={alt}
        className={className}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        {...(eager ? { fetchpriority: "high" } : {})}
        {...rest}
        onError={() => setErrored(true)}
      />
    </picture>
  );
}
